import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Modal,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { createAlbum, createContent } from "../../graphql/mutations";
import { toast } from "react-toastify";
import ViewCSVData from "./ViewCSVData";

import { v4 as uuidv4 } from "uuid";
import CSVUploader from "./CSVUploader";
import InputText from "../InputText";
import * as yup from "yup";
import { uploadData } from "aws-amplify/storage";
import { validateCSVHeaders } from "../../helper/csvHeaders";

// Styled component for file input
const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflow: "auto",
};

const client = generateClient();
const validationSchema = yup.object({
  title: yup
    .string("Enter the name of the series")
    .required("Name is required"),
  filePath: yup
    .string("Enter the release date")
    .required("Album Poster File Required! "),
});

const CreateAlbum = () => {
  const params = useParams();
  const [open, setOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const agreementForm = useSelector((state) => state.agreementForm);
  const contentForm = useSelector((state) => state.contentForm);
  const [csvData, setCSVData] = useState(null);

  const [masterFileContent, setMasterFileContent] = useState({
    filePath: "",
    file: "",
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };

  const handleFilePath = (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const guid = uuidv4();
    const type = contentForm.contentType;
    const category = contentForm.category;
    const title = csvData?.title?.[0]?.value || "";
    const filePath = `${type.replace(/\s+/g, "")}/${category.replace(
      /\s+/g,
      ""
    )}/${title.replace(/\s+/g, "")}/${guid}.${fileExtension}`;
    return { filePath };
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      const { filePath } = handleFilePath(uploadedFile);
      if (uploadedFile.type.startsWith("image/")) {
        setMasterFileContent({
          file: uploadedFile,
          filePath,
        });
        formik.setFieldValue("filePath", filePath);
      }
    }
  };

  const uploadMasterFile = async () => {
    try {
      const filePath = masterFileContent.filePath;
      await uploadData({
        key: filePath,
        data: masterFileContent.file,
      }).result;
    } catch (error) {
      console.error("An error occurred during the upload process:", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      plot: "",
      description: "",
      seasonNumber: "",
      releaseDate: "",
      imdbRating: "",
      rottenRating: "",
      filePath: "",
      currentCycle: params?.currentCycle || 1,
      rating: "",
      genre: [""],
      duration: 0.0,
      synopsis: "",
      year: "",
      director: "",
      actors: [""],
      posters: [""],
      seriesId: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await uploadMasterFile();
        
        const content = {
          agreementId: agreementForm?.id,
          title: csvData?.title?.[0]?.value,
          synopsis: csvData?.description?.[0]?.value,
          type: contentForm.contentType,
          albumId: contentForm?.albumId,
          category: contentForm.category,
          artist: csvData?.artist?.[0]?.value,
          status: "Pending",
          language: "eng",
          duration: csvData?.duration?.[0]?.value,
          filePath: masterFileContent.filePath,
          trailerFilePath: "",
          year: csvData?.year?.[0]?.value,
          posters: [masterFileContent.filePath],
          genre: csvData?.genre?.[0]?.value
            ?.split(",")
            .map((item) => item.trim()),
          isSeries: false,
          isDrmProtected: false,
          isAlbum: true,
        };

        const albumCreationReponse = await client.graphql({
          query: createAlbum,
          variables: {
            input: {
              title: formik?.values?.title,
              description: csvData?.description?.value,
            },
          },
        });
        
        if (albumCreationReponse?.data) {
          const response = await client.graphql({
            query: createContent,
            variables: {
              input: {
                ...content,
                albumId: albumCreationReponse.data.createAlbum.id,
                currentCycle: values?.currentCycle,
              },
            },
          });
          
          if (response.data.createContent) {
            toast.success("Album Metadata has been submitted!");
            handleClose();
          }
        }
      } catch (error) {
        toast.error("Internal Server Error!");
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const numberOfRefreshCycles = agreementForm?.numberOfRefreshCycles;
  let cyclesOptions = [];

  for (let i = 0; i < numberOfRefreshCycles; i++) {
    cyclesOptions.push({ value: i + 1, label: i + 1 });
  }

  const handleFileLoad = (csvData, fileInputRef) => {
    const result = validateCSVHeaders(csvData, contentForm.contentType);
    if (!result?.isValid) {
      result?.errors?.forEach((error) => {
        toast.warning(error);
      });
    }
    setCSVData(result?.mappedHeaders);
    console.log(csvData, "csvData");
    console.log(result, "result");
  };
  
  return (
    <>
      {/* Add Album Button */}
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<AddIcon />}
        sx={{ height: "60px" }}
      >
        Add Album
      </Button>

      {/* Create Album Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Card sx={{ boxShadow: "none" }}>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Create New Album
              </Typography>

              <Box
                component="form"
                onSubmit={formik.handleSubmit}
                sx={{ mt: 3 }}
              >
                <Grid container spacing={3}>
                  {/* Album Name */}
                  <Grid item xs={12}>
                    <InputText
                      label={"Album Title"}
                      formik={formik}
                      placeholder={"Enter album title"}
                      value={formik.values.title}
                      name={"title"}
                      type={"text"}
                      id={"title"}
                    />
                  </Grid>

                  {/* CSV File Upload */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{ p: 2, border: "1px dashed grey" }}
                    >
                      <CSVUploader
                        onFileLoad={(data, ref) => handleFileLoad(data, ref)}
                      />
                      {csvData ? (
                        <ViewCSVData csvData={csvData} />
                      ) : (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          No File Selected
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* Poster Uploads */}
                  <Grid item xs={12} md={12}>
                    <Paper
                      elevation={0}
                      sx={{ p: 2, border: "1px dashed grey" }}
                    >
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                      >
                        Upload Album Artwork
                        <VisuallyHiddenInput
                          type="file"
                          name="verticalPoster"
                          accept="image/*"
                          onChange={handleFileChange}
                          required
                        />
                      </Button>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        {masterFileContent?.file ? (
                          <img
                            src={URL.createObjectURL(masterFileContent?.file)}
                            alt="Album artwork preview"
                            style={{ maxWidth: "100%", maxHeight: "200px" }}
                          />
                        ) : (
                          "No file selected"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs={6}>
                      <Button
                        onClick={handleClose}
                        fullWidth
                        variant="outlined"
                        size="large"
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <LoadingButton
                        type="submit"
                        loading={loading}
                        variant="contained"
                        fullWidth
                        size="large"
                      >
                        Create Album
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Modal>
    </>
  );
};

export default CreateAlbum;